import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { SITE } from "../lib/site";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          O artigo que você procura não existe ou foi movido do arquivo.
        </p>
        <div className="mt-6">
          <Link to="/" className="btn-accent">Voltar para a capa</Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo deu errado. Você pode tentar novamente ou voltar para a capa.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="btn-primary"
          >
            Tentar novamente
          </button>
          <a href="/" className="btn-ghost">Ir para a capa</a>
        </div>
      </div>
    </div>
  );
}

const ROOT_TITLE = "Empresa Brasil Online — Marketing, vendas e comunicação digital";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: ROOT_TITLE },
      { name: "description", content: SITE.description },
      { name: "author", content: SITE.name },
      { name: "theme-color", content: "#E24E3A" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: ROOT_TITLE },
      { name: "twitter:title", content: ROOT_TITLE },
      { property: "og:description", content: SITE.description },
      { name: "twitter:description", content: SITE.description },
      { title: "Empresa Brasil Online" },
      { property: "og:title", content: "Empresa Brasil Online" },
      { name: "twitter:title", content: "Empresa Brasil Online" },
      { name: "description", content: "Guias, análises e conteúdos práticos sobre marketing digital, estratégias de vendas, comunicação, branding, mídia paga, conteúdo e CRM" },
      { property: "og:description", content: "Guias, análises e conteúdos práticos sobre marketing digital, estratégias de vendas, comunicação, branding, mídia paga, conteúdo e CRM" },
      { name: "twitter:description", content: "Guias, análises e conteúdos práticos sobre marketing digital, estratégias de vendas, comunicação, branding, mídia paga, conteúdo e CRM" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/6df06752-a7e0-4770-a204-fd89e662b74a" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/6df06752-a7e0-4770-a204-fd89e662b74a" },
    ],
    links: [
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-TZTYMWKGDS",
        async: true,
      },
      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-TZTYMWKGDS');",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "NewsMediaOrganization",
              "@id": "/#organization",
              name: SITE.name,
              logo: "/favicon.png",
              description: SITE.description,
              email: SITE.email,
            },
            {
              "@type": "WebSite",
              "@id": "/#website",
              name: SITE.name,
              description: SITE.description,
              inLanguage: "pt-BR",
              publisher: { "@id": "/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1"><Outlet /></main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </QueryClientProvider>
  );
}
